import * as React from 'react';
import { addMinutes } from 'date-fns';
import { Formik } from 'formik';
import { toast } from 'react-toastify';
import { Box, CircularProgress, Typography } from '@mui/material';
import SmallModal from '../global/SmallModal';
import Calendar from '../Scheduler/Calendar';
import useUser from '../../hooks/global/useUser';
import useWaitlistEntry from '../../hooks/cancellation-waitlist/useWaitlistEntry';
import useManuallyNotifyWaitlist from '../../hooks/cancellation-waitlist/useManuallyNotifyWaitlist';
import { getCalendarTimezone } from '../../helpers/appointments';
import { DEFAULT_APT_LENGTH } from '../../utils/form/appointmentForm';
import { LoadedUser } from '../../interfaces';

interface Props {
  waitlistEntryId: string;
  onClose: () => void;
}

function ManualNotifyDialog({ waitlistEntryId, onClose }: Props) {
  const { user } = useUser<LoadedUser>();
  const { data: entry, isError } = useWaitlistEntry(waitlistEntryId);
  const [startDateTime, setStartDateTime] = React.useState<Date | null>(null);

  // Nothing to render without the entry — bail out.
  React.useEffect(() => {
    if (isError) {
      toast.error('Failed to load the waitlist member.');
      onClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isError]);

  const durationMinutes =
    entry?.appointmentLengthInMinutes ?? parseInt(DEFAULT_APT_LENGTH, 10);

  const { mutate: notify, isPending } = useManuallyNotifyWaitlist({
    onSuccess: () => {
      toast.success(`${entry?.customerDisplayName} has been notified!`);
      onClose();
    },
    onError: (message: string) => {
      toast.error(message);
    },
  });

  const onSend = () => {
    if (!startDateTime) return;
    notify({
      waitlistEntryId,
      startDateTime: startDateTime.toISOString(),
      endDateTime: addMinutes(startDateTime, durationMinutes).toISOString(),
    });
  };

  return (
    <SmallModal
      open
      onClose={onClose}
      title="Notify About an Opening"
      footer={{
        button: {
          type: 'button',
          text: 'Send Notification',
          loading: isPending,
          disabled: !startDateTime || !entry,
          onClick: onSend,
        },
      }}>
      <Box p={2}>
        {!entry ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Pick an available time and we&apos;ll notify{' '}
              {entry.customerDisplayName} about it with a link to book.
            </Typography>
            {/* Stack the label above the field — the side-by-side .input row is
                wider than this modal (same treatment the form CSS applies <900px). */}
            <Box
              sx={{
                '& .input': { flexDirection: 'column' },
                '& .input label': {
                  width: '100%',
                  boxSizing: 'border-box',
                  bgcolor: 'transparent',
                  p: 0,
                  pb: 1,
                },
                '& .input input': {
                  border: theme => `1px solid ${theme.palette.divider}`,
                  width: '100%',
                  boxSizing: 'border-box',
                },
              }}>
              {/* Calendar's input reads formik context — provide one. */}
              <Formik initialValues={{ dateTime: '' }} onSubmit={() => {}}>
                <Calendar
                  label="Opening"
                  employeeId={entry.employeeId}
                  appointmentLengthInMinutes={String(durationMinutes)}
                  startDateTime={startDateTime?.toISOString()}
                  onChange={setStartDateTime}
                  disabled={false}
                  timezone={getCalendarTimezone(user ?? undefined)}
                  serviceCategory={entry.serviceCategory}
                  customerView
                />
              </Formik>
            </Box>
          </>
        )}
      </Box>
    </SmallModal>
  );
}

export default ManualNotifyDialog;
